import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  FormControlLabel,
  Switch,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Tabs,
} from '@mui/material';
import { HeaderBreadcrumbs, Iconify, Page, Scrollbar } from '@/components';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
} from '@/components/table';
import { SkeletonLoadingTable } from '@/components/skeleton';
import useSettings from '@/hooks/useSettings';
import useTable, { emptyRows, getComparator } from '@/hooks/useTable';
import useTabs from '@/hooks/useTabs';
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { PATH_USER } from '@/features/app';
import {
  deleteInAppPopup,
  getInAppPopupStats,
  getInAppPopups,
  updateInAppPopup,
} from '../api';
import {
  InAppPopupStatsDrawer,
  InAppPopupTableRow,
  InAppPopupsTableToolbar,
} from '../components';
import { inAppPopupsPath, ROOTS_IN_APP_POPUPS } from '../routes/paths';
import { InAppPopup, InAppPopupFilters } from '../types';
import {
  applySortFilterInAppPopups,
  IN_APP_POPUP_PERMISSION_MODULE,
  IN_APP_POPUPS_TABLE_HEAD,
} from '../utils';

const defaultFilters: InAppPopupFilters = {
  query: '',
  platform: '',
};

export default function InAppPopupsList() {
  const table = useTable({
    defaultOrderBy: 'priority',
    defaultOrder: 'desc',
  });
  const { themeStretch } = useSettings();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const permissions = useGetAccess(IN_APP_POPUP_PERMISSION_MODULE);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<InAppPopupFilters>(defaultFilters);
  const [statsPopup, setStatsPopup] = useState<InAppPopup | null>(null);
  const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } =
    useTabs('active');

  const activeFilter =
    filterStatus === 'active'
      ? true
      : filterStatus === 'inactive'
      ? false
      : undefined;

  const { data: popups, isLoading } = useQuery({
    queryKey: ['in-app-popups', activeFilter, filters.platform],
    queryFn: () =>
      getInAppPopups({
        active: activeFilter,
        platform: filters.platform || undefined,
      }),
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching in-app popups');
    },
  });

  const deletePopupMutation = useMutation({
    mutationFn: (id: string) => deleteInAppPopup(id),
    onSuccess: (_apiResponse, popupId) => {
      queryClient.setQueryData(['in-app-popups'], (old: InAppPopup[] = []) =>
        old.filter((popup) => popup.id !== popupId)
      );
      queryClient.invalidateQueries(['in-app-popups']);
      table.setPage(0);
      successMsg('Popup successfully deleted');
    },
    onError: (error) => {
      errorMsg(error, 'There was an error while deleting popup');
    },
  });

  const togglePopupMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      updateInAppPopup(id, { active }),
    onSuccess: (_apiResponse, variables) => {
      queryClient.invalidateQueries(['in-app-popups']);
      successMsg(
        `Popup successfully ${variables.active ? 'activated' : 'deactivated'}`
      );
    },
    onError: (error) => {
      errorMsg(error, 'There was an error while updating popup');
    },
  });

  const handleFilters = (name: keyof InAppPopupFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    table.setPage(0);
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
    table.setPage(0);
  };

  const dataFiltered = applySortFilterInAppPopups({
    tableData: popups ?? [],
    comparator: getComparator(table.order, table.orderBy as keyof InAppPopup),
    filters,
    filterStatus,
  });

  const paginatedPopups = dataFiltered.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage
  );
  const visiblePopupIds = paginatedPopups.map((popup) => popup.id);

  const { data: popupStats = {}, isFetching: isStatsFetching } = useQuery({
    queryKey: ['in-app-popup-stats', visiblePopupIds, filters.platform],
    queryFn: () =>
      getInAppPopupStats({
        popupIds: visiblePopupIds,
        platform: filters.platform || undefined,
      }),
    enabled: visiblePopupIds.length > 0,
    keepPreviousData: true,
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching popup stats');
    },
  });

  const denseHeight = table.dense ? 52 : 72;
  const isFiltered = !!filters.query || !!filters.platform;
  const isNotFound =
    !dataFiltered.length && (!!filters.query || !!filters.platform);

  return (
    <Page title="In-app Popups">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="In-app Popups"
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'In-app Popups', href: ROOTS_IN_APP_POPUPS },
            { name: 'List' },
          ]}
          action={
            permissions.canCreate && (
              <Button
                variant="contained"
                component={RouterLink}
                to={inAppPopupsPath('new')}
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                disabled={isLoading}
              >
                New Popup
              </Button>
            )
          }
        />

        <Card>
          <Tabs
            allowScrollButtonsMobile
            variant="scrollable"
            scrollButtons="auto"
            value={filterStatus}
            onChange={onChangeFilterStatus}
            sx={{ px: 2, bgcolor: 'background.neutral' }}
          >
            {[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'all', label: 'All' },
            ].map((tab) => (
              <Tab
                disableRipple
                key={tab.value}
                label={tab.label}
                value={tab.value}
              />
            ))}
          </Tabs>

          <Divider />

          <InAppPopupsTableToolbar
            filters={filters}
            onFilters={handleFilters}
            onResetFilters={handleResetFilters}
            isFiltered={isFiltered}
          />

          {isLoading ? (
            <SkeletonLoadingTable />
          ) : (
            <>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 1280, position: 'relative' }}>
                  <Table size={table.dense ? 'small' : 'medium'}>
                    <TableHeadCustom
                      order={table.order}
                      orderBy={table.orderBy}
                      headLabel={IN_APP_POPUPS_TABLE_HEAD}
                      rowCount={dataFiltered.length}
                      numSelected={table.selected.length}
                      onSort={table.onSort}
                    />
                    <TableBody>
                      {paginatedPopups.map((popup) => (
                        <InAppPopupTableRow
                          key={popup.id}
                          row={popup}
                          canCreate={permissions.canCreate}
                          canUpdate={permissions.canUpdate}
                          canDelete={permissions.canDelete}
                          stats={popupStats[popup.id]}
                          statsLoading={isStatsFetching}
                          onDuplicateRow={() =>
                            navigate(
                              `${inAppPopupsPath(
                                'new'
                              )}?duplicateFrom=${encodeURIComponent(popup.id)}`
                            )
                          }
                          onEditRow={() =>
                            navigate(
                              inAppPopupsPath('edit', {
                                popupId: popup.id,
                              })
                            )
                          }
                          onViewStats={() => setStatsPopup(popup)}
                          onDeleteRow={() =>
                            deletePopupMutation.mutate(popup.id)
                          }
                          onToggleActive={(active) =>
                            togglePopupMutation.mutate({
                              id: popup.id,
                              active,
                            })
                          }
                        />
                      ))}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={emptyRows(
                          table.page,
                          table.rowsPerPage,
                          dataFiltered.length
                        )}
                      />

                      <TableNoData
                        isNotFound={!dataFiltered.length}
                        title={
                          isNotFound
                            ? 'No popup found'
                            : 'No in-app popups available'
                        }
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <Box sx={{ position: 'relative' }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={dataFiltered.length}
                  rowsPerPage={table.rowsPerPage}
                  page={table.page}
                  onPageChange={table.onChangePage}
                  onRowsPerPageChange={table.onChangeRowsPerPage}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={table.dense}
                      onChange={table.onChangeDense}
                    />
                  }
                  label="Dense"
                  sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                />
              </Box>
            </>
          )}
        </Card>

        <InAppPopupStatsDrawer
          open={!!statsPopup}
          popup={statsPopup}
          stats={statsPopup ? popupStats[statsPopup.id] : undefined}
          onClose={() => setStatsPopup(null)}
        />
      </Container>
    </Page>
  );
}
